import React from 'react';
import _ from 'lodash';
import cx from 'classnames';
import moment from 'moment';


import { isStreamLive } from 'store/models/Stream';
import { ServiceTypes } from 'constants/constants';

import { StatItem, StatItemList, StatItemMoney, StatItemNumber, StatItemPercent, StatItemHours } from 'components/Stats';
import * as Icons from 'components/Svg';
import { EmptyItem } from 'components/EmptyItem';
import { LoadingStatItem } from 'components/Loading';
import ChannelCard from './ChannelCard';
import GameCard, { GameCardLoading } from 'components/GameCard';


let quant = (values, value, count) => {
  value = value ?? 0;
  let min = _.min(values);
  let max = _.max(values);
  let range = max - min;
  let step = range / count;
  let idx = Math.floor((value - min) / step) + 1;
  return idx;
}

let median = (arr) => {
  let mid = Math.floor(arr.length / 2);
  let nums = [...arr].sort((a, b) => a - b);
  let idx = arr.length % 2 !== 0 
    ? nums[mid] 
    : (nums[mid - 1] + nums[mid]) / 2;
  return idx;
}


// ====================
// ITEM
// ====================
export const StreamStatItem = ({ stream, session, stats, channel, showChannel = false, className, size, onClick, allValues, quantileCount = 4, ...props }) => {
  let start = moment(stream.start);
  let end = moment(stream.end ?? start);
  let hours = end.diff(start ?? end, 'minutes') |> #/60 |> _.round(#, 2);
  let isLive = isStreamLive(stream);
  let quantile = quant(allValues, stats?.streamAvgViewers, quantileCount);

  const onItemClick = () => onClick?.(stream);
  
  return (
    <div key={stream._id} className={cx("g-stream-stat-item", { live: isLive, clickable: _.isFunction(onClick) })} onClick={onItemClick}>
      <div className="stream-start">
        <div className="date">{ start.format("MMM DD, YYYY") }</div>
        <div className="time">
          <span>{ start.format("h:mm a") }</span>
          <Icons.RightChevronIcon size="s" />
          { isLive
           ? <span className="time-live">Live</span>
           : <span>{ end.format("h:mm a") }</span>}
        </div>
        { channel && showChannel &&
        <ChannelCard channel={channel} size="s" />}
      </div>
      <StatItemList>
        <StatItemHours label="Length" value={stats?.streamMinutes} />
        <StatItemNumber label="Avg. Viewers" value={stats?.streamAvgViewers} className={`quant-${quantileCount}-${quantile}`} />
        <StatItemNumber label="Peak Viewers" value={stats?.streamMaxViewers} />
        <StatItemMoney label="Media Value" value={stats?.totalMediaValue}/>
        <StatItemNumber label="Impressions" value={stats?.sceneViews} />
        <StatItemPercent label="Fill Rate" value={stats?.fillRate} />
        { channel?.service !== ServiceTypes.YOUTUBE && <>
        <StatItemNumber label="Clicks" value={stats?.clicks} />
        <StatItemPercent label="CTR" value={stats?.linkCtr} /></>}
      </StatItemList>
    </div>
  );
}
export default StreamStatItem;


export const StreamItem = ({ stream, session, stats, channel, games, showChannel = false, className, size, onClick, allValues, quantileCount = 4, ...props }) => {
  let start = moment(stream.start);
  let end = moment(stream.end ?? start);
  let hours = end.diff(start ?? end, 'minutes') |> #/60 |> _.round(#, 2);
  let isLive = isStreamLive(stream);
  const slicedGames = _.pick(games, _.keys(games)?.slice(0, 3))
  const leftoverCount = _.size(games) - 3 > 0 ? _.size(games) - 3 : null;
  const hasSlideshow = stats.sceneViews > 0;
  const hasChatbot = stats.messageCount > 0; 

  const onItemClick = () => onClick?.(stream);
  
  return (
    <div key={stream._id} className={cx("g-stream-item", { live: isLive, clickable: _.isFunction(onClick) } )} onClick={onItemClick}>
      <div className='stream-meta'>
        <div className="stream-info">
          <ChannelCard channel={channel} size="m" showName={true} />
          <div className="stream-date">
            <span className="date">{ start.format("MMM DD, YYYY") }</span>
          </div>
          <div className="stream-time">
            <span>{ start.format("h:mm a") }</span>
            <Icons.RightChevronIcon size="s" />
            { isLive
            ? <span className="time-live">Live</span>
            : <span>{ end.format("h:mm a") }</span>}
          </div>
          <div className='stream-features'>
            <Icons.SlideshowIcon size="m" color={hasSlideshow ? "on" : "off"} />
            <Icons.ChatIcon size="m" color={hasChatbot ? "on" : "off"} />
          </div>
        </div>
        <StatItemList>
          <StatItemHours label="Length" value={stats?.streamMinutes} />
          <StatItemNumber label="Peak Viewers" value={stats?.streamMaxViewers} />
          <StatItemNumber label="Impressions" value={stats?.sceneViews} />
        </StatItemList>
      </div>
      { !_.isEmpty(slicedGames) 
        ? <div className="stream-games">{_.map(slicedGames, game => <GameCard key={game._id} game={game} showName={false} size="s" />)}</div>
        : <div className="empty"></div>}
      { !_.isEmpty(slicedGames) &&  leftoverCount > 0 && <div className='games-extra'>+{leftoverCount}</div>}
    </div>
  );
}






// ====================
// LIST
// ====================
export const StreamStatList = ({ streams, stats, channels, onStreamClick, loading, loadingCount = 4, className, size, align, ...props }) => {

  if(!loading && _.isEmpty(streams)) 
    return (
      <EmptyItem>
        <p>There were no streams during this time period</p>
      </EmptyItem>);

  let avgViewers = streams
    |> _.map(#, '_id')
    |> _.pick(stats, #) 
    |> _.map(#, 'streamAvgViewers') 
  
  return (
    <div className={cx("g-stream-list", className, size, align)}>
      <div className="list-wrap">
        { loading && 
          _.map(_.fill(Array(loadingCount), '0'), (_, idx) => <LoadingStatItem key={idx} />) }

        { !loading && _.map(streams, stream =>
          <StreamStatItem
            key={stream._id}
            stream={stream}
            stats={stats[stream?._id]}
            channel={channels?.[stream.channelId]}
            size={size}
            onClick={onStreamClick}
            allValues={avgViewers}
            {...props} />)}
      </div>
    </div>)
}



export const StreamList = ({ streams, sessions, stats, channels, games, onStreamClick, loading, loadingCount = 4, className, size, align, ...props }) => {
  if (!loading && _.isEmpty(streams)) {
    return (
      <EmptyItem>
        <p>There were no streams during this time period</p>
      </EmptyItem>
    );
  }

  return (
    <div className={cx("g-stream-list", className, size, align)}>
      <div className="list-wrap">
        {loading &&
          _.map(_.fill(Array(loadingCount), '0'), (_, idx) => <LoadingStatItem key={idx} />)}

        {!loading &&
          _.map(streams, stream => {
            let streamGames = _.map(stream.games, 'gameId') |> _.pick(games, #);
            let session = _.find(sessions, { streamId: stream._id });
            return (
              <StreamItem
                key={stream._id}
                stream={stream}
                session={session}
                stats={stats[stream?._id]}
                channel={channels?.[stream.channelId]}
                games={streamGames}
                size={size}
                onClick={onStreamClick}
                {...props}
              />)})}
      </div>
    </div>
  );
};

  